/**
 * Leads the product page's locations line with the visitor's own state.
 *
 * `snippets/location-line.liquid` renders "Redeemable in New York, Los Angeles,
 * Chicago and 200+ US cities" from the merchant's list, complete, before this
 * file is even requested. This only ever rewrites the words: for a visitor
 * whose IP resolves to a US state with a known largest city, that city moves
 * to the front of the named cities (deduplicated against the merchant's own
 * entries), the same number of cities stay named so the line never reflows,
 * and the tail after them is re-rendered — recounted only if the merchant's
 * wording actually contains `{count}`. Any other outcome — a visitor outside
 * the US, an unknown state, a blocked, slow or failed lookup — leaves the
 * server-rendered line exactly as it was.
 *
 * While the lookup is in flight the city names shimmer in place
 * (`location-line--pending`, styled in the snippet) rather than showing one
 * list and then another: the swap read as a glitch. The class comes off in
 * every outcome, and a cached answer settles the line before first paint.
 *
 * Lookup: pro.ip-api.com when the merchant has set a key (Theme settings → API
 * Integrations → IP Geolocation), otherwise ipapi.co's free HTTPS endpoint. The
 * plain-HTTP ip-api.com fallback the earlier version carried is gone: the
 * storefront is served over HTTPS, where a mixed-content fetch is blocked
 * before it leaves the browser. The answer is kept in sessionStorage so one
 * visit costs one lookup, not one per product page.
 *
 * Wrapped in an IIFE: `assets/*.js` are classic scripts sharing one global
 * lexical scope, and a top-level `const` here would collide with the same name
 * in any other file (see CLAUDE.md).
 */
(function () {
  const STORAGE_KEY = 'ew:geo:v1';
  const FIELDS = 'status,countryCode,region,regionName';
  const PENDING_CLASS = 'location-line--pending';
  /* How long the line shimmers waiting for the lookup before it settles on
     the merchant's list as rendered. Shorter than the stylesheet's own 2.5s
     safety net, which only exists for the case where this file never runs. */
  const LOOKUP_TIMEOUT_MS = 1500;

  /**
   * Largest city by population in each US state (plus DC), keyed by the
   * two-letter code both lookups return. Names match the merchant's default
   * list so the dedupe below lines up ("New York", not "New York City").
   */
  const LARGEST_CITY_BY_STATE = {
    AL: 'Huntsville',
    AK: 'Anchorage',
    AZ: 'Phoenix',
    AR: 'Little Rock',
    CA: 'Los Angeles',
    CO: 'Denver',
    CT: 'Bridgeport',
    DE: 'Wilmington',
    DC: 'Washington DC',
    FL: 'Jacksonville',
    GA: 'Atlanta',
    HI: 'Honolulu',
    ID: 'Boise',
    IL: 'Chicago',
    IN: 'Indianapolis',
    IA: 'Des Moines',
    KS: 'Wichita',
    KY: 'Louisville',
    LA: 'New Orleans',
    ME: 'Portland',
    MD: 'Baltimore',
    MA: 'Boston',
    MI: 'Detroit',
    MN: 'Minneapolis',
    MS: 'Jackson',
    MO: 'Kansas City',
    MT: 'Billings',
    NE: 'Omaha',
    NV: 'Las Vegas',
    NH: 'Manchester',
    NJ: 'Newark',
    NM: 'Albuquerque',
    NY: 'New York',
    NC: 'Charlotte',
    ND: 'Fargo',
    OH: 'Columbus',
    OK: 'Oklahoma City',
    OR: 'Portland',
    PA: 'Philadelphia',
    RI: 'Providence',
    SC: 'Charleston',
    SD: 'Sioux Falls',
    TN: 'Nashville',
    TX: 'Houston',
    UT: 'Salt Lake City',
    VT: 'Burlington',
    VA: 'Virginia Beach',
    WA: 'Seattle',
    WV: 'Charleston',
    WI: 'Milwaukee',
    WY: 'Cheyenne',
  };

  /** Full state names, for a lookup that answers with the name but no code. */
  const STATE_CODE_BY_NAME = {
    alabama: 'AL',
    alaska: 'AK',
    arizona: 'AZ',
    arkansas: 'AR',
    california: 'CA',
    colorado: 'CO',
    connecticut: 'CT',
    delaware: 'DE',
    'district of columbia': 'DC',
    florida: 'FL',
    georgia: 'GA',
    hawaii: 'HI',
    idaho: 'ID',
    illinois: 'IL',
    indiana: 'IN',
    iowa: 'IA',
    kansas: 'KS',
    kentucky: 'KY',
    louisiana: 'LA',
    maine: 'ME',
    maryland: 'MD',
    massachusetts: 'MA',
    michigan: 'MI',
    minnesota: 'MN',
    mississippi: 'MS',
    missouri: 'MO',
    montana: 'MT',
    nebraska: 'NE',
    nevada: 'NV',
    'new hampshire': 'NH',
    'new jersey': 'NJ',
    'new mexico': 'NM',
    'new york': 'NY',
    'north carolina': 'NC',
    'north dakota': 'ND',
    ohio: 'OH',
    oklahoma: 'OK',
    oregon: 'OR',
    pennsylvania: 'PA',
    'rhode island': 'RI',
    'south carolina': 'SC',
    'south dakota': 'SD',
    tennessee: 'TN',
    texas: 'TX',
    utah: 'UT',
    vermont: 'VT',
    virginia: 'VA',
    washington: 'WA',
    'west virginia': 'WV',
    wisconsin: 'WI',
    wyoming: 'WY',
  };

  /** "Washington, DC" and "washington dc" are the same entry. */
  function cityKey(name) {
    return String(name || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  }

  function readCache() {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (error) {
      return null;
    }
  }

  function writeCache(value) {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(value));
    } catch (error) {
      // Storage full or blocked: the lookup simply runs again next page.
    }
  }

  /**
   * Normalises either provider's answer to `{ countryCode, stateCode }`, with
   * `stateCode` null for anything that is not a US state.
   */
  function normalise(data) {
    const countryCode = String(
      data.countryCode || data.country_code || data.country || '',
    ).toUpperCase();
    if (countryCode !== 'US') return { countryCode, stateCode: null };

    const rawCode = String(data.region_code || data.region || '').toUpperCase();
    let stateCode = LARGEST_CITY_BY_STATE[rawCode] ? rawCode : null;
    if (!stateCode) {
      const name = String(
        data.regionName || data.region || data.region_name || '',
      ).toLowerCase();
      stateCode = STATE_CODE_BY_NAME[name] || null;
    }
    return { countryCode, stateCode };
  }

  async function lookup(apiKey) {
    const url = apiKey
      ? `https://pro.ip-api.com/json/?key=${encodeURIComponent(
          apiKey,
        )}&fields=${FIELDS}`
      : 'https://ipapi.co/json/';

    // A lookup that has not answered by the time the shimmer is due to
    // settle is abandoned: the merchant's list shows, and the next page tries
    // again. Nothing is cached from an abandoned or failed lookup.
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), LOOKUP_TIMEOUT_MS);
    let response;
    try {
      response = await fetch(url, { signal: controller.signal });
    } finally {
      clearTimeout(timer);
    }
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const data = await response.json();
    if (data.status === 'fail' || data.error) {
      throw new Error(data.message || data.reason || 'Lookup failed');
    }

    const result = normalise(data);
    writeCache(result);
    return result;
  }

  /**
   * Rewrites the line in place. Exposed on the element as
   * `element.__ewLocationApply` purely so a console can exercise it.
   */
  function apply(element, city) {
    let configured = [];
    try {
      configured = JSON.parse(element.dataset.cities || '[]');
    } catch (error) {
      configured = [];
    }
    if (!Array.isArray(configured)) configured = [];

    const visibleCount = Math.max(
      1,
      Number(element.dataset.visible) || configured.length,
    );
    const key = cityKey(city);
    const cities = [
      city,
      ...configured.filter((entry) => cityKey(entry) !== key),
    ];

    const visible = cities.slice(0, visibleCount);
    const remaining = Math.max(0, cities.length - visible.length);

    const citiesEl = element.querySelector('[data-location-cities]');
    if (citiesEl) citiesEl.textContent = visible.join(', ');

    const moreEl = element.querySelector('[data-location-more]');
    if (moreEl) {
      const template = element.dataset.moreTemplate || '';
      // The tail is only a count when the merchant wrote it as one. The
      // default reads "and 200+ US cities", which is true however many named
      // cities the swap left over, so hiding it at `remaining === 0` would
      // delete a true claim. Same rule as `show_more` in
      // snippets/location-line.liquid — keep the two in step.
      const counts = template.includes('{count}');
      moreEl.textContent = template.replace('{count}', String(remaining));
      moreEl.hidden = template === '' || (counts && remaining < 1);
    }

    element.dataset.geoCity = city;
    document.dispatchEvent(
      new CustomEvent('everwow:geolocated', { detail: { city } }),
    );
  }

  async function init() {
    const element = document.querySelector(
      '[data-location-line][data-geolocate]',
    );
    if (!element) return;
    element.__ewLocationApply = (city) => apply(element, city);

    // Second product page of the visit: the answer is already known, so the
    // line is settled synchronously, before the browser paints the shimmer.
    const cached = readCache();
    if (cached && typeof cached === 'object') {
      const city = cached.stateCode && LARGEST_CITY_BY_STATE[cached.stateCode];
      if (city) apply(element, city);
      settle(element);
      return;
    }

    try {
      const { stateCode } = await lookup(element.dataset.apiKey || '');
      const city = stateCode && LARGEST_CITY_BY_STATE[stateCode];
      if (city) apply(element, city);
    } catch (error) {
      // Ad blockers and privacy extensions block these hosts routinely, and a
      // slow lookup is abandoned above; the server-rendered line is the
      // designed outcome in both cases, not an error state.
      console.debug('location-grabber: lookup skipped', error);
    } finally {
      settle(element);
    }
  }

  function settle(element) {
    element.classList.remove(PENDING_CLASS);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
